import type { IArticleConfig } from '~/config/article';
import widgetsConfig from '~/config/widgets.config';
import { debugLog } from '~/debug';
import CommunicationCreator from '~/modules/CommunicationCreator';
import type { ICommentsInterface } from '~/modules/iframes/Comments';
import type { IIframeInterface } from '~/modules/iframes/Iframe';
import NotificationAlert from '~/modules/NotificationAlert';
import type { ILoggerAction } from '~/modules/ServerLogger';
import ServerLogger from '~/modules/ServerLogger';
import Session from '~/modules/Session';
import TokenHandler from '~/modules/TokenHandler';
import type WidgetsCreator from '~/modules/WidgetsCreator';
import { getColorLuminance, getRealBackgroundColor } from '~/utils/color';
import { advancedKeys } from '~/config/keys';
import type { AudioWidgetListener } from './AudioWidgetListener';
import type { EmotesListener } from './EmotesListener';
import { Listener } from './Listener';
import type { ModalListener } from './ModalListener';
import type { PowerbarListener } from './PowerbarListener';
import type { QuizListener } from './QuizListener';
import { isStorageEnabled } from '~/utils/general';
import { safeLocalStorageGet, safeLocalStorageSetDelete } from '~/utils/localStorage';
import CommentsPanel from '~/modules/CommentsPanel';

// import { advancedKeys } from '~/config/keys';
// import EmailTargetPixel from '~/modules/EmailTargetPixel';

export class CommentsListener extends Listener {
  private modalIframe: IIframeInterface;
  private themeColor: string;
  private commentsPanel: CommentsPanel;
  private loadVuukleData: any;
  private isAdblockDetected: boolean;
  // private adObserverInitialized: boolean = false;
  constructor(
    protected widgetObject: IIframeInterface,
    private readonly index: string,
    private readonly articleConfig: IArticleConfig,
    private readonly widgetsCreator: WidgetsCreator,
    private readonly emotesListener: EmotesListener | undefined,
    private readonly powerbarListeners: PowerbarListener[] = [],
    private readonly audioWidgetListeners: AudioWidgetListener[] = [],
    private quizListener: QuizListener | undefined,
    private modalListener: ModalListener | undefined
  ) {
    super(widgetObject);
    window.addEventListener('message', this.listener);

    window.addEventListener('click', (event) => {
      const widgetContainer = widgetObject.iframe.parentElement;
      const clickedElement = event.target as HTMLElement | null;
      if (!clickedElement || !widgetContainer) return;
      if (clickedElement === widgetContainer || widgetContainer.contains(clickedElement)) return;
      this.postMessage({ closeCommentsModal: true });
    });

    this.postMessage({ canUseCookies: Session.canUseCookies });
    TokenHandler.addSSOTokenListener(`${widgetObject.id}-listener`, (token) => {
      this.postMessage({ authenticateUser: { token } });
    });
    TokenHandler.addSSOLogoutListener(`${widgetObject.id}-listener`, () => {
      this.postMessage({ authenticateUser: { signOut: true } });
    });
    if (articleConfig.amp) {
      if (articleConfig.comments.hideRecommendedArticles) {
        this.postMessage({ disableToT: true });
      }

      if (articleConfig.theme?.darkMode) {
        this.postMessage({ enableDarkMode: true });
      }

      const resizedInterval = setInterval(() => {
        if ((window as any).hasResized) {
          this.postMessage({ hasResized: true });
          clearInterval(resizedInterval);
        }
      }, 5000);
    }
    /** Send user token and token type */
    this.postMessage({ loginToken: TokenHandler.getToken() });

    /** Send the background color of the element that contains our widget so that it can decide whether it's switching to dark mode or light mode */
    let themeChangeAttempts = 50;
    const widgetThemeInterval = setInterval(() => {
      if (themeChangeAttempts === 0) {
        clearInterval(widgetThemeInterval);
        return;
      }
      const widgetContainer = widgetObject.iframe.parentElement;
      if (widgetContainer) {
        const bgColor = getRealBackgroundColor(widgetContainer);
        this.postMessage({ backgroundColor: bgColor });
        this.postMessage({ shouldUseDarkMode: getColorLuminance(widgetContainer) < 0.3 });
        clearInterval(widgetThemeInterval);
      }
      themeChangeAttempts--;
    }, 100);
    /** Hide the audio button from CW */
    if (this.audioWidgetListeners.length > 0) {
      this.postMessage({ showAudioButton: false });
    }
    /** Make CW compact in collapsed mode */
    if (advancedKeys[articleConfig.apiKey]?.compact) {
      this.postMessage({ compactMode: true });
    }
    this.postMessage({ isMobile: Session.isMobileUser });
    this.postMessage({ cookiesDisabled: !isStorageEnabled() });
    if (Session.userCountryDetermined) {
      const cookieUsageAgreed = safeLocalStorageGet('vuukle_cookie_usage_agreed') === 'true';
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        if (consentManager.default.isGDPR && typeof window.__tcfapi === 'undefined') {
          if (cookieUsageAgreed) {
            Session.canUseCookies = true;
            consentManager.default.hasConsent = true;
          } else {
            this.postMessage({ showCookieNotice: true });
          }
        }
        if (consentManager.default.isGDPR && typeof window.__tcfapi !== 'undefined') {
          consentManager.default.addVendorPresenceListener((isPresent) => {
            if (!isPresent) {
              this.postMessage({ showCookieNotice: true });
            }
          });
        }
      });
    } else {
      const interval = setInterval(() => {
        if (Session.userCountryDetermined) {
          const cookieUsageAgreed = safeLocalStorageGet('vuukle_cookie_usage_agreed') === 'true';
          import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
            if (consentManager.default.isGDPR && typeof window.__tcfapi === 'undefined') {
              if (cookieUsageAgreed) {
                Session.canUseCookies = true;
                consentManager.default.hasConsent = true;
              } else {
                this.postMessage({ showCookieNotice: true });
              }
            }
            if (consentManager.default.isGDPR && typeof window.__tcfapi !== 'undefined') {
              consentManager.default.addVendorPresenceListener((isPresent) => {
                if (!isPresent) {
                  this.postMessage({ showCookieNotice: true });
                }
              });
            }
          });
          clearInterval(interval);
        }
      }, 1000);
    }
  }

  /**
   * Comments widget messages listener
   * NOTE: We need to use arrow function here to bind function to this object instead of the event.
   * @param {any} event - message event
   */
  protected listener = async (event: any) => {
    if (!this.isValidEvent(event)) {
      return;
    }

    debugLog('log', '[Vuukle.js | Comments] message:', event);

    if (event.data.syncWidget instanceof Object) {
      this.synchronizeWidgets(event);
    }

    // 📊 BQ events reporting
    if (event.data.reportEvent) {
      this.reportToAnalyticsServer(event);
    }

    // 🕐 Check if even is valid realtime message
    if (typeof event.data.realtimeMessage === 'object') {
      // Register that the user has posted a comment. Needed for more accurate 'load new message' count for blue bar
      if (event.data.realtimeMessage.type === 'commentPosted') {
        CommunicationCreator.postedCommentCount++;
      }
      // this.realtime.send(event.data.realtimeMessage);
    }

    /** Receive Comment Input Box Rect data from comments widget */
    if (event.data.commentInputBoxRect) {
      CommunicationCreator.commentInputBoxRect = event.data.commentInputBoxRect;
    }

    /** If the user has clicked on 'load new comments' from inside comments widget */
    if (event.data.realtimeCommentsLoaded) {
      // Free the 'lock' so that the blue bar can show the 'load new messages' again
      CommunicationCreator.commentCountLock = false;
    }

    /** If user loaded reply or replies of a comment */
    if (event.data.realtimeRepliesLoaded && typeof event.data.realtimeRepliesLoaded === 'number') {
      // Add that to loaded reply count
      // Combine the properties inside the comments class to get access to commentCount
      // Maybe there should be a better way of doing this
      (this.widgetObject as IIframeInterface & ICommentsInterface).commentCount += event.data.realtimeRepliesLoaded;
    }

    /** If the user triggers first action for modal from inside comments widget */
    if (event.data.loadModal) {
      // lazy load modal
      if (event.data.loginTypes && this.articleConfig.amp && this.articleConfig.ampLoginTypes) {
        event.data.loginTypes = this.articleConfig.ampLoginTypes;
      }
      if (!this.modalListener) {
        await this.widgetsCreator.createModal(event.data);
      } else {
        this.modalListener.postMessage(event.data);
      }
      const widgetContainer = this.widgetObject.iframe.parentElement;
      this.modalListener?.switchThemeToDarkMode(getColorLuminance(widgetContainer) < 0.3);
      this.modalListener?.changeThemeColor(this.themeColor);
      this.modalListener?.sendAdblockDetectedMessage(this.isAdblockDetected);
      if (event.data.loginTypes) {
        // Notify powerbar that login types are sent to modal to prevent it from sending them again
        this.powerbarListeners.forEach((powerbarListener) => {
          powerbarListener.postMessage({ loginTypesSent: true });
        });
      }
    }

    // Share event with publisher if needed
    if (event.data.vuukle_event) {
      this.shareEventToPublished(event.data.vuukle_event, this.articleConfig.eventHandler);
    }

    // ℹ️ Article information received
    if (event.data.articleData instanceof Object) {
      this.updateCommentCounts(event);
    }

    if (event.data.settings && event.data.settings.theme && event.data.settings.theme.color) {
      this.updateTXColor(event);
    }

    // If anonymous token was received from Comments Widget
    if (event.data.anonToken) {
      TokenHandler.setAnonymousCommenting(true);
      TokenHandler.setRegularCommenting(false);
      TokenHandler.setGuestCommenting(false);
      TokenHandler.saveToken(event.data.anonToken);
      TokenHandler.setAnonToken(event.data.anonToken);
    }

    // If anon logout event was received from comments widget
    if (event.data.removeAnonToken) {
      TokenHandler.setAnonToken(null);
      TokenHandler.removeToken();
    }

    // If there's a new Session UID, update Session userAPIKey
    if (typeof event.data.updatedUID === 'string') {
      Session.userAPIKey = event.data.updatedUID;
      this.setUserID(event.data.updatedUID);
      this.emotesListener?.updateVotedEmote();
      this.powerbarListeners.forEach((listener) => {
        listener.setUserIdAvailable(true);
        listener.updateRecommendedState();
      });
    }

    // Received a message for modal
    if (event.data.modalData) {
      this.openModalWithMessage(event);
    }

    // SSO auth request
    if (event.data.callSSO) {
      TokenHandler.setSSOInfo(true);
      this.performSSOAuth();
    }

    if (event.data.openBrowserLink) {
      // Open Browser link
      window.location.href = event.data.openBrowserLink;
    }

    if (typeof event.data.isEmailVerified === 'boolean') {
      if (this.modalListener) {
        this.modalListener.postMessage({ isEmailVerified: event.data.isEmailVerified });
      }
    }

    if (typeof event.data.userId === 'string') {
      this.powerbarListeners.forEach((powerbar) => powerbar.postMessage({ userId: event.data.userId }));
    }

    // create an alert notification
    if (event.data.alertMessage) {
      NotificationAlert.createNotification(event.data.alertMessage.text, event.data.alertMessage.type);
    }

    /** Cookies are enabled when user does a successful regular login and disabled if they have don't consent */
    // if (typeof event.data.enableCookies === 'boolean') {
    //   this.changeCookieSettingsForWidgets(event.data.enableCookies);
    // }

    /** Send reported user data to other widgets */
    if (event.data.userInfo) {
      if (this.emotesListener) {
        this.emotesListener.postMessage({ userInfo: event.data.userInfo });
      }
    }

    if (event.data.loggedOut) {
      this.handleLogout();
    }
    // When the ad space DOMRect object is received, place the 1st ad inside
    // if (event.data.adSpaceRect) {
    //   this.placeAdInsideCW(event);
    // }

    if ('listenState' in event.data && !('firstClick' in event.data)) {
      const { listenState } = event.data;
      this.powerbarListeners.forEach((powerbarListener) => powerbarListener.postMessage({ listenState }));
      this.audioWidgetListeners.forEach((audioWidgetListener) => audioWidgetListener.postMessage({ listenState }));
    }

    if ('listenState' in event.data && 'firstClick' in event.data) {
      const { firstClick, listenState } = event.data;
      this.powerbarListeners.forEach((powerbarListener) => powerbarListener.postMessage({ listenState, firstClick }));
      this.audioWidgetListeners.forEach((audioWidgetListener) =>
        audioWidgetListener.postMessage({ listenState, firstClick })
      );
    }

    if ('audioSrc' in event.data) {
      this.shareAudioSyncMessage(event.data, 'audioSrc');
    }

    if ('currentTime' in event.data) {
      this.shareAudioSyncMessage(event.data, 'currentTime');
    }

    if ('hasEnded' in event.data) {
      this.shareAudioSyncMessage(event.data, 'hasEnded');
    }

    if ('welcomeAudioStarted' in event.data) {
      this.shareAudioSyncMessage(event.data, 'welcomeAudioStarted');
    }

    if ('welcomeAudioSrc' in event.data) {
      this.shareAudioSyncMessage(event.data, 'welcomeAudioSrc');
    }

    if ('welcomeAudioTime' in event.data) {
      this.shareAudioSyncMessage(event.data, 'welcomeAudioTime');
    }

    if (event.data.forgetToken) {
      TokenHandler.removeToken();
      this.powerbarListeners.forEach((powerbar) => {
        powerbar.postMessage({ loginToken: null });
      });
      this.emotesListener?.postMessage({ loginToken: null });
      this.modalListener?.postMessage({ removeSLToken: true });
      this.quizListener?.postMessage({ loginToken: null });
    }

    if (event.data.shareLoginTypes) {
      this.modalListener?.postMessage({
        loginTypes:
          this.articleConfig.amp && this.articleConfig.ampLoginTypes
            ? this.articleConfig.ampLoginTypes
            : event.data.shareLoginTypes,
      });
    }

    if (event.data.cookieUsageAgreed) {
      import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_cookie_usage_agreed', () => {
          safeLocalStorageSetDelete('vuukle_cookie_usage_agreed', 'true');
        });
        consentManager.default.hasConsent = true;
        consentManager.default.cookieConsentKnown = true;
      });
      Session.canUseCookies = true;
      this.quizListener?.postMessage({ cookieNoticeConfirmed: true });
      this.widgetsCreator.communicationCreator.confirmCookieNotice();
    }

    // Let SDK webview know that load more articles is clicked
    if (event.data.loadMoreArticlesClicked && this.articleConfig.sdk) {
      (window as any).webkit?.messageHandlers?.LoadMoreArticles?.postMessage('Load more articles');
      console.log('Load more articles clicked');
    }

    // Let SDK webview know that load more comments is clicked
    if (event.data.loadMoreCommentsClicked && this.articleConfig.sdk) {
      (window as any).webkit?.messageHandlers?.LoadMoreComments?.postMessage('Load more comments');
      console.log('Load more comments clicked');
    }

    // Listen current scroll Y request from iframe
    if (event.data.type === 'getScrollY') {
      const scrollY = window.scrollY;
      const offsetTop = document.querySelector(event.data.selector).offsetTop;
      this.postMessage({ type: 'scrollYResponse', scrollY, offsetTop });
    }

    /** Load personalized ad js pixel in the page */
    // if (event.data.hashedEm) {
    //   new EmailTargetPixel(event.data.hashedEm);
    // }
  };

  /**
   * Update the 'vuukle_user_id' localStorage key with the new user ID
   * @param {string} userId User ID to save
   */
  private async setUserID(userId: string) {
    const consentManager = await import(/* webpackChunkName: "cmg" */ '~/modules/ConsentManager');
    if (Session.canUseCookies) {
      consentManager.default.executeWhenStorageValueCanBeSet('vuukle_user_id', () => {
        safeLocalStorageSetDelete('vuukle_user_id', userId);
      });
    } else {
      Session.addStorageUpdateCallback(() => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_user_id', () => {
          safeLocalStorageSetDelete('vuukle_user_id', userId);
        });
      });
    }
  }

  public setLoadVuukleData(data: any) {
    this.loadVuukleData = data;
    this.commentsPanel?.sendLoadVuukleData(data);
  }

  public setThemeColor(color: string) {
    this.themeColor = color;
  }

  public showCommentsPanel() {
    if (typeof this.commentsPanel === 'undefined') {
      this.commentsPanel = new CommentsPanel(
        this.widgetObject,
        `${this.index}-2`,
        this.articleConfig,
        this.loadVuukleData,
        this.widgetsCreator
      );
    }
    this.commentsPanel.showPanel();
  }

  private shareAudioSyncMessage(eventObject: any, dataToShare: string) {
    const data = eventObject[dataToShare];
    this.powerbarListeners.forEach((powerbarListener) => powerbarListener.postMessage({ [dataToShare]: data }));
    this.audioWidgetListeners.forEach((audioListener) => audioListener.postMessage({ [dataToShare]: data }));
  }

  private updateCommentCounts(event: any) {
    const { articleData } = event.data;
    CommunicationCreator.fillVuukleCountElement(this.index, articleData.commentCount || 0);
    // 🕐 Send Realtime Event to inform about current comment count
    // this.realtime.send({ type: RealtimeMessageTypes.SetCommentCount, commentCount: articleData.commentCount });
  }

  private reportToAnalyticsServer(event: any) {
    const { action } = event.data.reportEvent;

    // Log action to server for analytics
    if (typeof action === 'string') {
      ServerLogger.log(action as ILoggerAction, this.articleConfig);
    }
  }

  // private placeAdInsideCW(event: any) {
  //   const { top } = event.data.adSpaceRect;
  //   const iframeParent = this.widgetObject.iframe.parentElement;
  //   const firstAd = iframeParent?.querySelector('.vuukle-ads') as HTMLDivElement;
  //   if (firstAd) {
  //     const adjustBy = event.data.adjustBy || 0;
  //     // Tell CW to make space for the ad, add 10px for space between the ad and the load more button
  //     this.postMessage({ adHeight: firstAd.offsetHeight + 10 });
  //     // Add 10 extra height to make sure it's under the last comment
  //     let adTop = top + adjustBy + 10;
  //     firstAd.style.position = 'relative';
  //     firstAd.style.top = adTop + 'px';
  //     firstAd.style.left = '50%';
  //     firstAd.style.transform = 'translate(-50%)';
  //     // Negative margin bottom removes whitespace at the original spot of the ad that is left by position: relative
  //     firstAd.style.marginBottom = `-${firstAd.offsetHeight}px`;

  //     if (!this.adObserverInitialized) {
  //       /** On ad resize/change - post new ad height to CW  */
  //       const callback = () => {
  //         if (firstAd.offsetHeight !== 0) {
  //           this.postMessage({ adHeight: firstAd.offsetHeight + 10 });
  //         }
  //       };

  //       /** Use ResizeObserver with a fallback to Mutation Observer */
  //       if (typeof window.ResizeObserver !== 'undefined') {
  //         const resizeObserver = new window.ResizeObserver(callback);
  //         resizeObserver.observe(firstAd);
  //       } else {
  //         /** Use MutationObserver to notify CW of ad height changes every time the ad container updates */
  //         const config = { childList: true, subtree: true, attributes: true };
  //         const observer = new MutationObserver(callback);
  //         observer.observe(firstAd, config);
  //       }

  //       this.adObserverInitialized = true;
  //     }
  //   }
  // }

  private performSSOAuth() {
    /** SSO methods */
    const ssoAuth = this.articleConfig.comments?.auth?.sso?.onClick || this.articleConfig.auth?.sso?.onClick;
    /** Call auth or show warning that it's not configured */
    typeof ssoAuth === 'function'
      ? ssoAuth()
      : console.error(`[Vuukle] ⚠ Please configure SSO to allow users to authenticate.`);
  }

  private openModalWithMessage(event: any) {
    if (this.modalListener) {
      // 🗳️ Modal Data | Open modal message with details of what to show
      this.modalListener.postMessage({
        modalData: {
          ...event.data.modalData,
          data: {
            ...event.data.modalData.data,
            ...(event.data.modalData.type === 'login' ? { loginFrom: 'comments' } : {}),
          },
        },
        loginTypes:
          this.articleConfig.amp && this.articleConfig.ampLoginTypes
            ? this.articleConfig.ampLoginTypes
            : event.data.loginTypes,
      });
      // We could just change styles of Modal instance directly but for unknown reasons
      // it's not working on some sites. Mb some conflicts with plugins
      const modalNode: HTMLElement | null = document.querySelector(`#${this.modalIframe.id}`);
      if (modalNode) {
        modalNode.style.display = event.data.modalData.hideModal ? 'none' : 'block';
      }
    }
  }

  private handleLogout() {
    import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
      /** On logout - check if we still have the user consent for keeping data in browser storage. If we don't - remove all cookies/localStorage items */
      if ((consentManager.default.isGDPR || consentManager.default.isCCPA) && !Session.canUseCookies) {
        this.deleteCookies();
      }
    });
    if (this.emotesListener) {
      this.emotesListener.postMessage({ userInfo: { apiKey: null } });
      this.emotesListener.updateVotedEmote();
    }
    if (this.quizListener) {
      this.quizListener.postMessage({ loginToken: null });
    }
    if (TokenHandler.isSSO) {
      const logoutFunction = this.articleConfig.auth?.sso?.onLogout || this.articleConfig.comments.auth?.sso?.onLogout;
      if (logoutFunction) {
        logoutFunction();
      }
    }
  }

  private synchronizeWidgets(event: any) {
    /**
     * We need powerbarListener to update their comment count whenever a comment is added
     */
    if (event.data.syncWidget.commentAdded) {
      this.powerbarListeners.forEach((powerbarListener) =>
        powerbarListener.postMessage({ syncWidget: { commentAdded: event.data.syncWidget.commentAdded } })
      );
    }

    /**
     * Add comments to powerbar counter when such update is received
     */
    if (event.data.syncWidget.addCommentsToCounter) {
      this.powerbarListeners.forEach((powerbarListener) =>
        powerbarListener.postMessage({
          syncWidget: { addCommentsToCounter: event.data.syncWidget.addCommentsToCounter },
        })
      );
    }
  }

  /**
   * Update the color theme of TX widget
   * @param event
   */
  private updateTXColor(event: any) {
    const trafficExchangeWrapper = document.querySelector(widgetsConfig.selectors.trafficExchange.concat(this.index));

    if (trafficExchangeWrapper) {
      /** Insert a background color altering style for TX read more button */
      const styleTag = document.createElement('style');
      styleTag.innerHTML = `
        #${trafficExchangeWrapper.id} .vuukle-tx-read-more {
          background: ${event.data.settings.theme.color};
        }
      `;
      document.head.appendChild(styleTag);
    }
  }

  /** Tell widgets to remove everything they've set in user's storage */
  private deleteCookies() {
    this.postMessage({ deleteCookies: true });

    if (this.emotesListener) {
      this.emotesListener.removeCookies();
    }

    this.powerbarListeners.forEach((powerbarListener) => {
      powerbarListener.postMessage({ deleteCookies: true });
      powerbarListener.removeCookies();
    });

    if (this.modalListener) {
      this.modalListener.postMessage({ deleteCookies: true });
    }

    Session.deleteCookies();
  }

  /**
   * Share event with publisher if configured
   * @param {any} event - event to share with eventHandler
   * @param {any} eventHandler - possible event handler function that publisher added to the article config
   * @returns {void}
   */
  private shareEventToPublished(event: any, eventHandler: any): void {
    if (typeof eventHandler === 'function') {
      eventHandler(event);
    }
  }

  /**
   * Updates the modal iframe instance to be used by CW listener
   * @param modalIframe
   * @param modalListener
   */
  public updateModalInstance(modalIframe: IIframeInterface, modalListener: ModalListener) {
    this.modalListener = modalListener;
    this.modalIframe = modalIframe;
  }

  public updateQuizListenerInstance(quizListener: QuizListener) {
    this.quizListener = quizListener;
  }

  public setIsAdblockDetected(detected: boolean) {
    this.isAdblockDetected = detected;
  }

  /**
   * Change widget theme
   * @param theme Light or dark mode
   */
  public changeTheme(theme: 'light' | 'dark') {
    this.postMessage({ changeTheme: theme });
  }

  /**
   * Add a click listener on an element for showing the comments panel
   * @param selector Element selector
   */
  public addCustomTriggerForCommentsPanel(selector: string) {
    const targetElement = document.querySelector(selector);
    if (targetElement) {
      targetElement.addEventListener('click', () => {
        this.showCommentsPanel();
      });
    }
  }
}
